import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiFehler } from "../api/client";
import { schluessel, useAnlage, useExtraktionWiederholen, useMastr, useMastrSpeichern, useNetzbetreiberErmitteln } from "../api/hooks";
import { HELFER_URL, HelferFehler, mastrHelfer } from "../api/mastrHelfer";
import type { AusfuellErgebnis, Dokument, HelferStatus, MastrDaten, NetzbetreiberErmittlung } from "../api/typen";
import { Hinweis, Karte, Knopf, Modal, Spinner, StatusBadge } from "../komponenten/Basis";
import { KonfidenzAnzeige, KonfidenzLegende } from "../komponenten/Konfidenz";
import { useToast } from "../kontext/ToastKontext";
import { datum, kwp, MASTR_STATUS_TEXT, NETZBETREIBER_STATUS_TEXT } from "../lib/format";
import { AMPEL_KLASSEN, GEPRUEFT, ampel } from "../lib/konfidenz";
import {
  BETREIBER_FELDER,
  MASTR_LABEL,
  NETZ_FELDER,
  TECHNIK_FELDER,
  formAusMastr,
  geaenderteMastrFelder,
  mastrBody,
  pruefeSeeNummer,
  trenneStrasse,
  validiereMastr,
  type MastrFeld,
  type MastrFeldDef,
  type MastrForm,
} from "../lib/mastrFelder";

// Nach der Anmeldung bleiben nur die Netzbetreiber-Angaben bearbeitbar.
const NACH_ANMELDUNG_AENDERBAR: MastrFeld[] = ["netzbetreiber_status", "netzbetreiber_eingereicht_am", "netzbetreiber_referenz"];

export function MastrSeite() {
  const { id } = useParams();
  const anlageId = Number(id);
  const abfrage = useMastr(anlageId);

  if (!Number.isInteger(anlageId) || anlageId <= 0) return <Hinweis art="fehler">Ungültige Anlagen-ID.</Hinweis>;
  if (abfrage.isLoading) return <div className="flex justify-center py-24"><Spinner label="MaStR-Daten werden geladen…" /></div>;
  if (abfrage.error || !abfrage.data) {
    return (
      <div className="mx-auto max-w-lg space-y-4 py-12">
        <Hinweis art="fehler">{abfrage.error?.message ?? "MaStR-Daten konnten nicht geladen werden."}</Hinweis>
        <Knopf onClick={() => void abfrage.refetch()}>Erneut laden</Knopf>
      </div>
    );
  }
  return <MastrAnsicht daten={abfrage.data} />;
}

// ---------------------------------------------------------------- Formularfeld

function Feld({
  def, wert, fehler, konfidenz, geaendert, gesperrt, beiAenderung,
}: {
  def: MastrFeldDef; wert: string; fehler?: string; konfidenz?: number; geaendert: boolean; gesperrt: boolean;
  beiAenderung: (w: string) => void;
}) {  // prettier-ignore
  const id = `mastr-${def.name}`;
  const hatWert = wert.trim() !== "";
  const stufe = konfidenz === undefined ? "keine" : ampel(geaendert ? GEPRUEFT : konfidenz, hatWert);
  const rahmen = fehler
    ? "border-ampel-rot bg-ampel-rotHell/40"
    : geaendert
      ? "border-sky-500 bg-sky-50/50"
      : stufe === "rot" || stufe === "gelb"
        ? `border-2 ${AMPEL_KLASSEN[stufe].rahmen}`
        : "border-neutral-300";
  const klassen = `block w-full rounded-lg border bg-white px-3 py-2 text-sm shadow-sm outline-none focus:ring-2 focus:ring-neutral-900 disabled:bg-neutral-50 disabled:text-neutral-600 ${rahmen}`;
  return (
    <div className={def.breit ? "sm:col-span-2" : ""}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <label htmlFor={id} className="text-xs font-semibold text-neutral-700">
          {def.label}
          {def.pflicht && <span className="text-ampel-rot" title="Für das MaStR-Formular nötig"> *</span>}
          {def.einheit && <span className="font-normal text-neutral-400"> ({def.einheit})</span>}
        </label>
        {geaendert ? (
          <span className="text-xs font-semibold text-sky-700">✎ geändert</span>
        ) : (
          konfidenz !== undefined && <KonfidenzAnzeige konfidenz={konfidenz} hatWert={hatWert} />
        )}
      </div>
      {def.art === "janein" || def.art === "auswahl" ? (
        <select id={id} className={klassen} value={wert} disabled={gesperrt} onChange={(e) => beiAenderung(e.target.value)}>
          {def.optionen?.map((o) => <option key={o.wert} value={o.wert}>{o.text}</option>)}
        </select>
      ) : (
        <input
          id={id}
          type={def.art === "datum" ? "date" : "text"}
          inputMode={def.art === "zahl" ? "decimal" : def.art === "ganzzahl" ? "numeric" : undefined}
          autoComplete="off"
          className={klassen}
          value={wert}
          placeholder={def.platzhalter}
          disabled={gesperrt}
          aria-invalid={Boolean(fehler)}
          onChange={(e) => beiAenderung(e.target.value)}
        />
      )}
      {fehler && <p className="mt-1 text-xs text-ampel-rot">{fehler}</p>}
    </div>
  );
}

// ------------------------------------------------------------- MaStR-Helfer

function useHelferStatus(): { status: HelferStatus | null; fehler: string | null; pruefen: () => void } {
  const [status, setStatus] = useState<HelferStatus | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [zaehler, setZaehler] = useState(0);
  useEffect(() => {
    let aktiv = true;
    const abfragen = () =>
      mastrHelfer
        .status()
        .then((s) => aktiv && (setStatus(s), setFehler(null)))
        .catch((e: HelferFehler) => aktiv && (setStatus(null), setFehler(e.message)));
    void abfragen();
    const intervall = window.setInterval(abfragen, 4000);
    return () => {
      aktiv = false;
      window.clearInterval(intervall);
    };
  }, [zaehler]);
  return { status, fehler, pruefen: () => setZaehler((z) => z + 1) };
}

function StatusZeile({ ok, text }: { ok: boolean; text: string }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      <span className={ok ? "text-ampel-gruen" : "text-neutral-400"} aria-hidden>{ok ? "●" : "○"}</span>
      <span className={ok ? "" : "text-neutral-500"}>{text}</span>
    </li>
  );
}

function Protokoll({ ergebnis }: { ergebnis: AusfuellErgebnis }) {
  const offen = ergebnis.eintraege.filter((e) => e.status === "offen");
  const ok = ergebnis.eintraege.filter((e) => e.status === "ok");
  return (
    <div className="space-y-3">
      <Hinweis art={offen.length ? "warnung" : "erfolg"}>
        <p className="font-semibold">
          {ok.length} Angaben gesetzt{offen.length ? ` · ${offen.length} bitte im MaStR prüfen/ergänzen` : " · nichts offen"}
        </p>
        <p className="mt-1">{ergebnis.hinweis}</p>
      </Hinweis>
      {offen.length > 0 && (
        <ul className="space-y-1 rounded-lg border border-ampel-gelb/30 bg-ampel-gelbHell/50 p-3 text-xs">
          {offen.map((e, i) => (
            <li key={i}><span className="font-semibold">{e.abschnitt}:</span> {e.text}</li>
          ))}
        </ul>
      )}
      <details>
        <summary className="cursor-pointer text-xs font-semibold text-neutral-600">Gesetzte Angaben anzeigen ({ok.length})</summary>
        <ul className="mt-2 space-y-0.5 text-xs text-neutral-600">
          {ok.map((e, i) => <li key={i}>✓ <span className="text-neutral-400">{e.abschnitt}:</span> {e.text}</li>)}
        </ul>
      </details>
    </div>
  );
}

// ------------------------------------------------------------------ Ansicht

function MastrAnsicht({ daten }: { daten: MastrDaten }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const speichern = useMastrSpeichern(daten.anlage_id);
  const ermitteln = useNetzbetreiberErmitteln(daten.anlage_id);
  const anlage = useAnlage(daten.anlage_id);
  const extraktion = useExtraktionWiederholen();
  const helfer = useHelferStatus();

  const basis = useMemo(() => formAusMastr(daten), [daten]);
  const [form, setForm] = useState<MastrForm>(basis);
  // Neue Serverdaten (Speichern, Netzbetreiber ermitteln, Neu-Extraktion) nur in Felder
  // übernehmen, die gerade nicht bearbeitet werden – ungespeicherte Eingaben bleiben erhalten.
  const vorherigeBasis = useRef(basis);
  useEffect(() => {
    const alt = vorherigeBasis.current;
    if (alt === basis) return;
    setForm((f) => {
      const neu = { ...f };
      for (const k of Object.keys(basis) as MastrFeld[]) if (f[k] === alt[k]) neu[k] = basis[k];
      return neu;
    });
    vorherigeBasis.current = basis;
  }, [basis]);
  const [serverFehler, setServerFehler] = useState<Partial<Record<MastrFeld, string>>>({});
  const [ausfuellErgebnis, setAusfuellErgebnis] = useState<AusfuellErgebnis | null>(null);
  const [ermittlung, setErmittlung] = useState<NetzbetreiberErmittlung | null>(null);
  const [helferLaeuft, setHelferLaeuft] = useState(false);
  const [liestAus, setLiestAus] = useState(false);
  const [registrierenOffen, setRegistrierenOffen] = useState(false);
  const [seeNummer, setSeeNummer] = useState(daten.mastr_nummer ?? "");
  const [registriertAm, setRegistriertAm] = useState(daten.mastr_registriert_am ?? new Date().toISOString().slice(0, 10));

  const geaendert = useMemo(() => new Set(geaenderteMastrFelder(basis, form)), [basis, form]);
  const clientFehler = useMemo(() => validiereMastr(form), [form]);
  const fehler = { ...daten.validierung, ...serverFehler, ...clientFehler } as Partial<Record<MastrFeld, string>>;
  const angemeldet = daten.anlage_status === "angemeldet";
  const gesperrt = (name: MastrFeld) => !daten.bearbeitbar && !NACH_ANMELDUNG_AENDERBAR.includes(name);
  const rechnungen = (anlage.data?.dokumente ?? []).filter((d) => d.typ === "rechnung" || d.typ === "formular");

  function setzen(name: MastrFeld, wert: string) {
    setServerFehler(({ [name]: _weg, ...rest }) => rest);
    setForm((alt) => ({ ...alt, [name]: wert }));
  }

  async function sichern(): Promise<boolean> {
    if (Object.keys(clientFehler).length) {
      toast.fehler(new Error(Object.entries(clientFehler).map(([f, m]) => `${MASTR_LABEL[f]}: ${m}`).join(" · ")), "Bitte Eingaben korrigieren");
      return false;
    }
    if (geaendert.size === 0) return true;
    try {
      await speichern.mutateAsync(mastrBody(form, [...geaendert], daten.version));
      toast.erfolg("Gespeichert", `${geaendert.size} Angabe(n) übernommen`);
      return true;
    } catch (e) {
      if (e instanceof ApiFehler && e.status === 422) setServerFehler(e.feldFehler as Partial<Record<MastrFeld, string>>);
      toast.fehler(e, e instanceof ApiFehler && e.code === "version_conflict" ? "Parallele Änderung – bitte neu laden" : "Speichern fehlgeschlagen");
      return false;
    }
  }

  function adresseUebernehmen() {
    const v = daten.betreiber_adresse_vorschlag;
    const [strasse, nummer] = v ? [v.betreiber_strasse ?? "", v.betreiber_hausnummer ?? ""] : trenneStrasse(daten.standort_strasse ?? "");
    setForm((alt) => ({
      ...alt,
      betreiber_strasse: strasse,
      betreiber_hausnummer: nummer,
      betreiber_plz: v?.betreiber_plz ?? daten.standort_plz ?? "",
      betreiber_ort: v?.betreiber_ort ?? daten.standort_ort ?? "",
    }));
  }

  function netzbetreiberErmitteln() {
    ermitteln.mutate(undefined, {
      onSuccess: (e) => {
        setErmittlung(e);
        const treffer = e.anzahl_treffer > 1 ? ` (${e.anzahl_treffer} Treffer)` : "";
        if (e.adresse_exakt) toast.erfolg("Netzbetreiber ermittelt", `${e.netzbetreiber}${treffer} – für „${e.gefundene_adresse}“`);
        else toast.info("Netzbetreiber übernommen – bitte prüfen", `Adresse nicht exakt gefunden, Treffer: „${e.gefundene_adresse}“`);
      },
      onError: (e) => toast.fehler(e, "Netzbetreiber konnte nicht ermittelt werden"),
    });
  }

  async function ausRechnungAuslesen() {
    setLiestAus(true);
    try {
      const ergebnisse: Dokument[] = [];
      for (const dok of rechnungen) ergebnisse.push(await extraktion.mutateAsync(dok.id));
      await queryClient.invalidateQueries({ queryKey: schluessel.mastr(daten.anlage_id) });
      // Die API antwortet auch bei gescheiterter Extraktion mit 200 – Status des Dokuments zählt.
      const gescheitert = ergebnisse.filter((d) => d.extraktion_status === "fehlgeschlagen");
      const erstes = gescheitert[0];
      if (!erstes) {
        toast.erfolg("Technische Daten neu ausgelesen", "Geprüfte Werte wurden nicht überschrieben.");
      } else {
        toast.fehler(
          new Error(`${erstes.original_dateiname}: ${erstes.fehler ?? "Extraktion fehlgeschlagen"}${erstes.hinweis ? ` – ${erstes.hinweis}` : ""}`),
          gescheitert.length === ergebnisse.length ? "Auslesen fehlgeschlagen" : `${gescheitert.length} von ${ergebnisse.length} Dokumenten nicht ausgelesen`,
        );
      }
    } catch (e) {
      toast.fehler(e, "Auslesen fehlgeschlagen");
    } finally {
      setLiestAus(false);
    }
  }

  async function browserOeffnen() {
    try {
      const r = await mastrHelfer.browserOeffnen();
      toast.info(r.status === "bereits_offen" ? "MaStR-Browser ist bereits offen" : "MaStR-Browser wird geöffnet", "Einloggen und den Registrierungsassistenten für eine Solaranlage öffnen.");
      helfer.pruefen();
    } catch (e) {
      toast.fehler(e, "Browser konnte nicht geöffnet werden");
    }
  }

  async function ausfuellen() {
    if (!(await sichern())) return;
    setHelferLaeuft(true);
    setAusfuellErgebnis(null);
    try {
      const ergebnis = await mastrHelfer.ausfuellen(daten.anlage_id);
      setAusfuellErgebnis(ergebnis);
      toast.erfolg("MaStR-Formular ausgefüllt", ergebnis.offen.length ? `${ergebnis.offen.length} Punkt(e) bitte im MaStR prüfen` : "Bitte prüfen und selbst absenden");
      if (daten.mastr_status === "nicht_begonnen") {
        speichern.mutate({ version: daten.version, mastr_status: "in_bearbeitung" });
      }
    } catch (e) {
      toast.fehler(e, "Ausfüllen fehlgeschlagen");
    } finally {
      setHelferLaeuft(false);
    }
  }

  function registrieren() {
    const meldung = pruefeSeeNummer(seeNummer) ?? (seeNummer.trim() ? null : "Bitte die MaStR-Nummer der Einheit eintragen");
    if (meldung) {
      toast.fehler(new Error(meldung), "MaStR-Nummer prüfen");
      return;
    }
    speichern.mutate(
      { version: daten.version, mastr_status: "registriert", mastr_nummer: seeNummer.replace(/\s/g, "").toUpperCase(), mastr_registriert_am: registriertAm },
      {
        onSuccess: () => {
          setRegistrierenOffen(false);
          toast.erfolg("Anlage angemeldet", "MaStR-Registrierung gespeichert. Anlagendaten sind jetzt gesperrt.");
        },
        onError: (e) => toast.fehler(e, "Registrierung konnte nicht gespeichert werden"),
      },
    );
  }

  function registrierungZuruecknehmen() {
    if (!window.confirm("Registrierung wirklich zurücknehmen? Die Anlage wird wieder auf „Final“ gesetzt.")) return;
    speichern.mutate(
      { version: daten.version, mastr_status: "in_bearbeitung" },
      { onSuccess: () => toast.info("Registrierung zurückgenommen"), onError: (e) => toast.fehler(e, "Zurücknehmen fehlgeschlagen") },
    );
  }

  const feldProps = (def: MastrFeldDef, mitKonfidenz = false) => ({
    def,
    wert: form[def.name],
    fehler: fehler[def.name],
    konfidenz: mitKonfidenz ? daten.konfidenz[def.name] : undefined,
    geaendert: geaendert.has(def.name),
    gesperrt: gesperrt(def.name),
    beiAenderung: (w: string) => setzen(def.name, w),
  });

  const fehlend = daten.fehlende_angaben.map((f) => MASTR_LABEL[f] ?? f);
  const helferBereit = Boolean(helfer.status?.registrierungsseite_offen);

  return (
    <div className="space-y-5 pb-24">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to={`/anlagen/${daten.anlage_id}`} className="text-sm text-neutral-500 hover:text-neutral-900">← Zurück zur Prüfung</Link>
          <h1 className="mt-1 flex flex-wrap items-center gap-3 text-2xl font-semibold tracking-tight">
            MaStR & Netzbetreiber · Anlage #{daten.anlage_id} · {daten.kunde_name}
            <StatusBadge status={daten.anlage_status} />
          </h1>
          <p className="mt-1 text-xs text-neutral-500">
            MaStR: <strong>{MASTR_STATUS_TEXT[daten.mastr_status]}</strong>
            {daten.mastr_nummer && ` (${daten.mastr_nummer})`} · Netzbetreiber: <strong>{NETZBETREIBER_STATUS_TEXT[daten.netzbetreiber_status]}</strong>
            {" · "}Leistung {kwp(daten.leistung_kwp)} · Standort {daten.standort_strasse}, {daten.standort_plz} {daten.standort_ort}
          </p>
        </div>
        <KonfidenzLegende />
      </div>

      {daten.warnungen.map((w) => <Hinweis key={w} art="warnung">⚠ {w}</Hinweis>)}
      {angemeldet ? (
        <Hinweis art="erfolg">Die Anlage ist angemeldet (MaStR-Nr. {daten.mastr_nummer}, {datum(daten.mastr_registriert_am)}). Anlagen- und Betreiberdaten sind gesperrt.</Hinweis>
      ) : fehlend.length > 0 ? (
        <Hinweis art="info">Für das MaStR-Formular fehlen noch: <strong>{fehlend.join(", ")}</strong></Hinweis>
      ) : (
        <Hinweis art="erfolg">Alle Angaben für das MaStR-Formular sind vorhanden.</Hinweis>
      )}

      <div className="grid gap-5 xl:grid-cols-2">
        <div className="space-y-5">
          <Karte className="p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Anlagenbetreiber</h2>
              {daten.bearbeitbar && (daten.betreiber_adresse_vorschlag || daten.standort_strasse) && (
                <Knopf variante="geist" className="px-2 py-1 text-xs" onClick={adresseUebernehmen}>↧ Adresse vom Anlagenstandort übernehmen</Knopf>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {BETREIBER_FELDER.map((def) => <Feld key={def.name} {...feldProps(def)} />)}
              <div className="sm:col-span-2 text-xs text-neutral-500">E-Mail für das MaStR: {daten.email}</div>
            </div>
          </Karte>

          <Karte className="p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Technische Daten</h2>
              {daten.bearbeitbar && rechnungen.length > 0 && (
                <Knopf variante="geist" className="px-2 py-1 text-xs" laedt={liestAus} onClick={() => void ausRechnungAuslesen()}>
                  ↻ Aus Rechnung neu auslesen (Claude)
                </Knopf>
              )}
            </div>
            <p className="mb-3 text-xs text-neutral-500">Bruttoleistung laut Prüfung: <strong>{kwp(daten.leistung_kwp)}</strong></p>
            <div className="grid gap-4 sm:grid-cols-2">
              {TECHNIK_FELDER.filter((d) => d.name !== "speicher_kapazitaet_kwh" || form.speicher_vorhanden === "ja").map((def) => (
                <Feld key={def.name} {...feldProps(def, true)} />
              ))}
            </div>
            {daten.rechnungsdatum && !gesperrt("inbetriebnahme_datum") && (
              <p className="mt-3 rounded-lg bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
                {form.inbetriebnahme_datum === daten.rechnungsdatum ? (
                  <>
                    Inbetriebnahmedatum = Rechnungsdatum ({datum(daten.rechnungsdatum)}). Bitte prüfen und bei Abweichung oben
                    das richtige Datum eintragen.
                  </>
                ) : (
                  <>
                    Rechnungsdatum: {datum(daten.rechnungsdatum)} ·{" "}
                    <button type="button" className="font-semibold underline" onClick={() => setzen("inbetriebnahme_datum", daten.rechnungsdatum as string)}>
                      als Inbetriebnahmedatum übernehmen
                    </button>
                  </>
                )}
              </p>
            )}
          </Karte>
        </div>

        <div className="space-y-5">
          <Karte className="p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500">Netzbetreiber</h2>
              {daten.bearbeitbar && (
                <Knopf variante="geist" className="px-2 py-1 text-xs" laedt={ermitteln.isPending} onClick={netzbetreiberErmitteln}>
                  ⌖ Automatisch ermitteln (VNBdigital)
                </Knopf>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {NETZ_FELDER.map((def) => <Feld key={def.name} {...feldProps(def)} />)}
            </div>
            {ermittlung && !ermittlung.adresse_exakt && form.netzbetreiber === ermittlung.netzbetreiber && (
              <div className="mt-4">
                <Hinweis art="warnung">
                  VNBdigital hat „{daten.standort_strasse}“ nicht exakt gefunden und den Netzbetreiber für „{ermittlung.gefundene_adresse}“
                  (gleiche PLZ) geliefert. Im selben PLZ-Gebiet ist er meist derselbe – bitte kurz prüfen.
                </Hinweis>
              </div>
            )}
            {daten.portal ? (
              <div className="mt-4 rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm">
                <p className="font-semibold">{daten.portal.name}</p>
                <p className="mt-0.5 text-xs text-neutral-600">{daten.portal.hinweis}</p>
                <a href={daten.portal.anmelde_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm font-semibold underline underline-offset-2">
                  Zum Anmeldeportal ↗
                </a>
              </div>
            ) : (
              daten.netzbetreiber && <p className="mt-3 text-xs text-neutral-500">Für diesen Netzbetreiber ist noch kein Anmeldeportal hinterlegt.</p>
            )}
          </Karte>

          <Karte className="p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-500">MaStR-Registrierung</h2>
            {angemeldet ? (
              <div className="space-y-3">
                <p className="text-sm">
                  Registriert als <strong className="font-mono">{daten.mastr_nummer}</strong> am {datum(daten.mastr_registriert_am)}.
                </p>
                <Knopf variante="gefahr" onClick={registrierungZuruecknehmen} laedt={speichern.isPending}>Registrierung zurücknehmen</Knopf>
              </div>
            ) : (
              <div className="space-y-4">
                <ul className="space-y-1">
                  <StatusZeile ok={Boolean(helfer.status)} text={helfer.status ? `MaStR-Helfer läuft (v${helfer.status.version})` : "MaStR-Helfer läuft nicht"} />
                  <StatusZeile ok={Boolean(helfer.status?.browser_offen)} text="MaStR-Browser geöffnet" />
                  <StatusZeile ok={helferBereit} text="Registrierungsassistent (Solaranlage) geöffnet" />
                </ul>
                {!helfer.status && (
                  <Hinweis art="info">
                    Doppelklick auf <strong>tools\mastr-helfer\MaStR-Helfer starten.bat</strong> im Projektordner und das Fenster offen lassen.
                    Erwartet unter <span className="font-mono">{HELFER_URL}</span>.
                  </Hinweis>
                )}
                <div className="flex flex-wrap gap-2">
                  <Knopf onClick={() => void browserOeffnen()} disabled={!helfer.status}>MaStR-Browser öffnen</Knopf>
                  <Knopf
                    variante="primaer"
                    onClick={() => void ausfuellen()}
                    disabled={!helferBereit || daten.anlage_status !== "final"}
                    laedt={helferLaeuft}
                    title={daten.anlage_status !== "final" ? "Anlage zuerst bestätigen" : undefined}
                  >
                    {helferLaeuft ? "Fülle Formular aus…" : "Formular im MaStR ausfüllen"}
                  </Knopf>
                </div>
                <p className="text-xs text-neutral-500">Die Ausfüllhilfe klickt nie „Registrieren“. Abschicken bleibt deine Entscheidung.</p>
                {ausfuellErgebnis && <Protokoll ergebnis={ausfuellErgebnis} />}
                <div className="border-t border-neutral-200 pt-4">
                  <Knopf onClick={() => setRegistrierenOffen(true)} disabled={daten.anlage_status !== "final"}>
                    Registrierung abgeschlossen – MaStR-Nummer eintragen
                  </Knopf>
                </div>
              </div>
            )}
          </Karte>
        </div>
      </div>

      {(daten.bearbeitbar || geaendert.size > 0) && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-neutral-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
            <span className="text-xs text-neutral-500">
              {geaendert.size ? `${geaendert.size} ungespeicherte Änderung(en)` : "Keine ungespeicherten Änderungen"}
              {Object.keys(clientFehler).length > 0 && <span className="text-ampel-rot"> · {Object.keys(clientFehler).length} Eingabefehler</span>}
            </span>
            <div className="ml-auto flex gap-2">
              <Knopf variante="geist" disabled={geaendert.size === 0} onClick={() => setForm(basis)}>Verwerfen</Knopf>
              <Knopf variante="primaer" disabled={geaendert.size === 0} laedt={speichern.isPending} onClick={() => void sichern()}>Speichern</Knopf>
            </div>
          </div>
        </div>
      )}

      <Modal
        offen={registrierenOffen}
        titel="MaStR-Registrierung abschließen"
        beiSchliessen={() => setRegistrierenOffen(false)}
        fuss={
          <>
            <Knopf onClick={() => setRegistrierenOffen(false)}>Abbrechen</Knopf>
            <Knopf variante="primaer" onClick={registrieren} laedt={speichern.isPending}>Als angemeldet speichern</Knopf>
          </>
        }
      >
        <div className="space-y-4 text-sm">
          <p>Trage die MaStR-Nummer ein, die das Portal nach dem Absenden für die <strong>Solareinheit</strong> vergeben hat. Danach gilt die Anlage als angemeldet und ihre Daten sind gesperrt.</p>
          <div>
            <label htmlFor="see" className="mb-1 block text-xs font-semibold">MaStR-Nummer der Einheit</label>
            <input id="see" className="w-full rounded-lg border border-neutral-300 px-3 py-2 font-mono text-sm" placeholder="SEE…" value={seeNummer} onChange={(e) => setSeeNummer(e.target.value)} data-autofokus />
            {seeNummer && pruefeSeeNummer(seeNummer) && <p className="mt-1 text-xs text-ampel-rot">{pruefeSeeNummer(seeNummer)}</p>}
          </div>
          <div>
            <label htmlFor="registriert-am" className="mb-1 block text-xs font-semibold">Registriert am</label>
            <input id="registriert-am" type="date" className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" value={registriertAm} onChange={(e) => setRegistriertAm(e.target.value)} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
